import { Injectable } from '@angular/core';
import { PCFShadowMap, PerspectiveCamera, Scene, Vector3, WebGLRenderer } from 'three';
import { RenderCameraHelper } from '@src/app/services/render/render-camera.helper';
import { ViewMode } from '@src/app/model/view-mode';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RenderService } from '@src/app/services/render/render.service';

@Injectable({
  providedIn: 'root',
})
export class ProductImageService {
  constructor(
    private _renderService: RenderService,
  ) {
  }

  // currently the picture on JG website is 220 x 169, so we want to keep the same ratio (roughly)
  public static readonly SCREENSHOT_WIDTH = 880;
  public static readonly SCREENSHOT_HEIGHT = 680;
  public static FILENAME = 'product-image.png'; // Default filename for the screenshot

  public async generateProductImage$(scene: Scene): Promise<File> {
    return new Promise((resolve, reject) => {
      const offscreenRenderer = this._createOffscreenRenderer();
      const camera = new PerspectiveCamera();
      const controls = new OrbitControls(camera);
      const configurationGroup = RenderCameraHelper.getConfigurationGroupFromScene(scene);

      RenderCameraHelper.fitCameraToConfiguration(
        scene,
        ViewMode.cleanNice,
        offscreenRenderer,
        camera,
        controls,
        configurationGroup,
        null,
        new Vector3(5.4, 3.0, 3.6) // slightly smaller box than the default to avoid cutting off edges
      );

      const whiteBgScene = this._renderService.addNewBackgroundToScene(scene, 0xffffff, 0xffffff, 0xffffff);
      offscreenRenderer.render(whiteBgScene, camera);

      requestAnimationFrame(() => {
        try {
          const imageDataURL
            = offscreenRenderer.domElement.toDataURL('image/png'); // Or 'image/jpeg' for smaller file size=
          const file = ProductImageService.dataURLToFile(imageDataURL, ProductImageService.FILENAME);
          // this._debugImageDownload(imageDataURL); // Product image download, enable for debugging
          resolve(file);
        } catch (error) {
          console.error('Error generating or downloading image:', error);
          reject(error);
        } finally {
          // cleanup to avoid memory leaks
          camera.clear();
          offscreenRenderer.dispose();
        }
      });
    });
  }

  /**
   * Triggers a download of the image for debugging purposes.
   * @param imageDataURL The data URL of the image to download.
   */
  private _debugImageDownload(imageDataURL: string) {
    const link = document.createElement('a');
    link.href = imageDataURL;
    link.download = ProductImageService.FILENAME;
    document.body.appendChild(link); // Required for Firefox to trigger download
    link.click();
    document.body.removeChild(link); // Clean up the temporary link
  }

  public static dataURLToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Creates an offscreen WebGLRenderer for rendering the scene to an image.
   */
  private _createOffscreenRenderer(): WebGLRenderer {
    const offscreenRenderer = new WebGLRenderer({
      alpha: true,
      antialias: true,
    });
    offscreenRenderer.setSize(ProductImageService.SCREENSHOT_WIDTH, ProductImageService.SCREENSHOT_HEIGHT);
    offscreenRenderer.setPixelRatio(window.devicePixelRatio);
    offscreenRenderer.outputColorSpace = 'srgb-linear';

    // Turn this on/off depending on if we want shadow in the screenshot
    offscreenRenderer.shadowMap.enabled = true;
    offscreenRenderer.shadowMap.type = PCFShadowMap;

    return offscreenRenderer;
  }
}
